import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { removeFromCart, addToCart, clearCart } from "../redux/cartSlice";
import { X, Plus, Minus } from "lucide-react";
import CheckoutPopup from "./CheckoutPopup";
import { addOrder } from "../redux/ordersSlice";

const CartPage = () => {
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const subscription = useSelector((state) => state.subscription);

  const [selectedProduct, setSelectedProduct] = useState(null);

  const totalPrice = cartItems.reduce(
    (total, item) =>
      subscription.discount
        ? total + (item.price - (item.price * subscription.discount) / 100) * item.quantity
        : total + item.price * item.quantity,
    0
  );

  const getFinalPrice = (price) => {
    return subscription.discount
      ? Math.round(price - (price * subscription.discount) / 100)
      : price;
  };

  const handlePaymentComplete = (product, qty, paymentMethod, address) => {
    const finalAmount = getFinalPrice(product.price) * qty;

    dispatch(
      addOrder({
        id: `ORD${Math.floor(Math.random() * 10000)}`,
        item: product.title,
        amount: `₹${finalAmount.toLocaleString()}`,
        status: "Processing",
        qty,
        paymentMethod,
        address,
        date: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString(),
        thumbnail: product.thumbnail,
      })
    );

    setSelectedProduct(null);
    dispatch(removeFromCart(product.id)); // Remove from cart after purchase
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold mb-4">Your Cart is Empty 🛒</h1>
        <p className="text-gray-500">Add products to see them here.</p>
      </div>
    );
  }

  return (
    <div className="p-8 min-h-screen bg-gray-100">
      <h1 className="text-3xl font-bold mb-6">Your Cart</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {cartItems.map((item) => (
          <div
            key={item.id}
            className="bg-white p-4 rounded-xl shadow flex justify-between items-center"
          >
            <div>
              <h2 className="font-semibold text-lg">{item.title}</h2>
              <p className="text-gray-600">
                ₹{getFinalPrice(item.price).toLocaleString()}
                {subscription.discount > 0 && (
                  <span className="line-through text-gray-400 ml-2">
                    ₹{item.price.toLocaleString()}
                  </span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() =>
                  item.quantity > 1
                    ? dispatch(addToCart({ ...item, quantity: -1 }))
                    : dispatch(removeFromCart(item.id))
                }
                className="p-1 bg-gray-200 rounded hover:bg-gray-300"
              >
                <Minus size={16} />
              </button>

              <span className="font-semibold">{item.quantity}</span>

              <button
                onClick={() => dispatch(addToCart(item))}
                className="p-1 bg-gray-200 rounded hover:bg-gray-300"
              >
                <Plus size={16} />
              </button>

              <button
                onClick={() => dispatch(removeFromCart(item.id))}
                className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
              >
                <X size={16} />
              </button>

              <button
                onClick={() => setSelectedProduct(item)}
                className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Buy
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="max-w-4xl mx-auto mt-8 flex justify-between items-center bg-white p-4 rounded-xl shadow">
        <h2 className="text-xl font-bold">Total: ₹{totalPrice.toLocaleString()}</h2>
        <button
          onClick={() => dispatch(clearCart())}
          className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
        >
          Clear Cart
        </button>
      </div>

      {/* CHECKOUT MODAL */}
      {selectedProduct && (
        <CheckoutPopup
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onPaymentComplete={handlePaymentComplete}
        />
      )}
    </div>
  );
};

export default CartPage;
